import React, { useState } from 'react';
import { motion, AnimatePresence } from "motion/react";
import {  CalendarIcon, ClockIcon, TicketIcon } from '@/components/icons';
import { getSignedMediaUrl, formatDateByTimezone, formatTime, formatDuration, getTimezoneAbbreviation } from '@/lib/utils';
import { TicketCard } from './TicketCard';
import type { BookingResponse } from '@/features/booking/types/bookingTypes';
import type { EventScheduleResponse } from '@/features/booking/types/eventBookingCategoryTypes';

interface EventScheduleCardProps {
  eventSchedule: EventScheduleResponse;
  bookings: Array<{ booking: BookingResponse }>;
  onBookingClick: (bookingId: string) => void;
}



export const EventScheduleCard: React.FC<EventScheduleCardProps> = ({ 
  eventSchedule, 
  bookings,
  onBookingClick 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { event, start_time, duration } = eventSchedule;
  const timezone = eventSchedule.timezone_id;
  
  const imageUrl = getSignedMediaUrl(event.card_image_id, 'image').url;
  const bookingCount = bookings.length;

  return (
    <motion.div 
      className="bg-gray-900 border border-gray-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{ 
        y: -4,
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
        transition: { type: "spring", stiffness: 300, damping: 30 }
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8
      }}
    >
      {/* Event Card Header */}
      <motion.div 
        className="relative h-32 sm:h-48 lg:h-64 bg-cover bg-center bg-no-repeat cursor-pointer"
        style={{
          backgroundImage: `url(${imageUrl})`,
        }}
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        {/* Overlay */}
        <motion.div 
          className="absolute inset-0 bg-black/60"
          whileHover={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          transition={{ duration: 0.3 }}
        ></motion.div>
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-between p-3 sm:p-6 lg:p-8">
          {/* Event Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <h3 className="text-sm sm:text-lg lg:text-2xl font-serif font-bold text-white mb-1 sm:mb-2 line-clamp-2">
              {event.name}
            </h3>
            <p className="text-gray-300 text-xs sm:text-sm line-clamp-1 sm:line-clamp-2 hidden sm:block">
              {event.description}
            </p>
          </motion.div>
          
          {/* Schedule Info */}
          <motion.div 
            className="space-y-1 sm:space-y-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <motion.div 
              className="flex items-center gap-1 sm:gap-2 text-white"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <motion.div 
                className="w-3 h-3 sm:w-4 sm:h-4"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <CalendarIcon />
              </motion.div>
              <span className="text-xs sm:text-sm font-medium truncate">
                {formatDateByTimezone(start_time, timezone, { format: 'date', includeTimezone: false })} {getTimezoneAbbreviation(start_time, timezone)}
              </span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-1 sm:gap-2 text-white"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <motion.div 
                className="w-3 h-3 sm:w-4 sm:h-4"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <ClockIcon />
              </motion.div>
              <span className="text-xs sm:text-sm font-medium">{formatTime(start_time, timezone, false)}</span>
              <span className="text-xs sm:text-sm text-gray-300 hidden sm:inline">•</span>
              <span className="text-xs sm:text-sm text-gray-300 hidden sm:inline">{formatDuration(duration)}</span>
            </motion.div>
            <motion.div 
              className="flex items-center gap-1 sm:gap-2 text-white"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <motion.div 
                className="w-3 h-3 sm:w-4 sm:h-4"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <TicketIcon />
              </motion.div>
              <span className="text-xs sm:text-sm font-medium">{bookingCount} booking{bookingCount !== 1 ? 's' : ''}</span>
            </motion.div>
          </motion.div>
          
          {/* Expand/Collapse Button - Positioned absolutely */}
          <motion.button 
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 bg-violet-600 hover:bg-violet-500 text-white p-2 sm:p-3 rounded-full shadow-lg z-10"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
              <motion.div 
                className="w-2 h-2 border-r-2 border-b-2 border-white"
                animate={{ rotate: isExpanded ? 225 : 45 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              ></motion.div>
            </div>
          </motion.button>
        </div>
      </motion.div>
      
      {/* Expanded Tickets Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            className="border-t border-gray-800"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              duration: 0.4, 
              ease: "easeInOut",
              opacity: { duration: 0.2 }
            }}
          >
            <motion.div 
              className="p-3 sm:p-4 lg:p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <motion.div 
                className="space-y-2 sm:space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-white mb-2 sm:mb-4">
                  Your Bookings ({bookingCount})
                </h4>
                <motion.div 
                  className="space-y-2 sm:space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  {bookings
                    .filter((item) => item?.booking != null)
                    .map((item, index) => {
                      const booking = item.booking;
                      return (
                      <motion.div
                        key={booking.id || `booking-${index}`}
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ 
                          delay: 0.4 + index * 0.1, 
                          duration: 0.4,
                          type: "spring",
                          stiffness: 300,
                          damping: 25
                        }}
                      >
                        <TicketCard 
                          booking={booking}
                          onCardClick={() => {
                            if (booking?.id) {
                              onBookingClick(booking.id);
                            }
                          }} 
                        />
                      </motion.div>
                    );
                    })}
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
